import { useMemo } from 'react';
import {
  ExitToApp,
  AccountCircle,
  PeopleAltOutlined,
  AccountTreeOutlined,
  QuestionAnswerOutlined,
  AddIcCall,
  AccountBalance,
  MenuBookOutlined,
} from '@mui/icons-material';
import DomainAddIcon from '@mui/icons-material/DomainAdd';
import SchemaIcon from '@mui/icons-material/Schema';

import {
  SipTrunkPage,
  OrganizationPage,
  RolesAndPermissionsPage,
  UsersAndOrganizationsPage,
  AgentPage,
  AgentsPage,
  NewAgentPage,
  ConversationPage,
  ConversationsPage,
  KnowledgeManagementPage,
  SchemaEditorPage,
  CallManagementPage,
  ModelsManagementPage,
  ConversationsBillingPage,
  AgentsBillingPage,
  ConversationBillingDetailsPage,
  KnowledgeSourceList,
  BatchCallListPage,
  CallTaskWizardPage,
  CallsManagementPage,
  PromptLibraryPage,
  AnalyticsPage,
} from '../../pages';
import { MenuLabel } from './const';
import type { MenuItem } from './Layout';
import {
  AnalyticsIcon,
  BatchCallIcon,
  FolderIcon,
  RetryCallIcon,
  UserGroupIcon,
} from '../../assets/icons';
import { PERMISSIONS } from '../../shared/constants/permissions';

export const getAuthenticatedMenuItems = (logout: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Analytics,
    pageTitle: 'Analytics',

    url: '/analytics',
    icon: <AnalyticsIcon />,
    component: AnalyticsPage,
    action: [
      PERMISSIONS.VIEW_DASHBOARD_ANALYTICS,
      PERMISSIONS.VIEW_WIDGET_ANALYTICS,
    ],
  },
  {
    menuTitle: MenuLabel.ManageAgents,
    pageTitle: 'Agents List',

    url: '/agents/:organization_id',
    icon: <PeopleAltOutlined />,
    component: AgentsPage,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.Conversations,
    pageTitle: 'Conversations List',

    url: '/conversation',
    icon: <QuestionAnswerOutlined />,
    component: ConversationsPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Conversation',

    url: '/conversation/:id',
    component: ConversationPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    pageTitle: 'Create new Agent',

    url: '/agents/:organization_id/new',
    component: NewAgentPage,
    action: PERMISSIONS.CREATE_AGENTS,
  },
  {
    pageTitle: 'Agent Configuration',

    url: '/agents/:organization_id/:agent_id',
    icon: <AccountCircle />,
    component: AgentPage,
    action: PERMISSIONS.VIEW_AGENTS,
  },
  {
    menuTitle: MenuLabel.KnowledgeManagement,
    pageTitle: 'Knowledge Management',

    url: '/knowledge_management',
    icon: <FolderIcon />,
    component: KnowledgeManagementPage,
    action: [PERMISSIONS.MANAGE_KNOWLEDGE, PERMISSIONS.MANAGE_SCRAPER],
  },
  {
    pageTitle: 'Knowledge Details',

    url: '/knowledge_management/:id',
    component: KnowledgeSourceList,
    action: PERMISSIONS.MANAGE_KNOWLEDGE,
  },
  {
    pageTitle: 'Organization',

    url: '/org_management/organization/:id',
    component: OrganizationPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.CallManagement,
    pageTitle: 'Call Management',

    url: '/call_management',
    component: CallManagementPage,
    icon: <RetryCallIcon />,
    action: [PERMISSIONS.MAKE_CALL, PERMISSIONS.MAKE_CONFERENCE_CALL],
  },
  {
    menuTitle: MenuLabel.RoleManagement,

    url: '/role_management',
    icon: <AccountTreeOutlined />,
    component: RolesAndPermissionsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.OrgManagement,

    url: '/org_management',
    icon: <UserGroupIcon />,
    component: UsersAndOrganizationsPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.JSONManagement,

    url: '/json_management',
    component: SchemaEditorPage,
    icon: <SchemaIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.ModelsManagement,

    url: '/models_management',
    component: ModelsManagementPage,
    icon: <DomainAddIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.SipTrunk,

    url: '/sip_trunk',
    icon: <AddIcCall />,
    component: SipTrunkPage,
    action: PERMISSIONS.MANAGE_SIP_TRUNK,
  },
  {
    menuTitle: MenuLabel.ConversationsBilling,

    url: '/billing',
    component: AgentsBillingPage,
    icon: <AccountBalance />,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    menuTitle: MenuLabel.PromptLibrary,

    url: '/prompt_library',
    component: PromptLibraryPage,
    icon: <MenuBookOutlined />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Conversations Billing',

    url: '/billing/:agent_id',
    component: ConversationsBillingPage,
    action: PERMISSIONS.VIEW_BILLING,
  },
  {
    pageTitle: 'Conversation',
    url: '/billing/:agent_id/conversations/:conversation_id',
    component: ConversationBillingDetailsPage,
    action: PERMISSIONS.VIEW_CONVERSATIONS,
  },
  {
    menuTitle: MenuLabel.BatchCalls,
    pageTitle: 'Mass Outreach Tasks',

    url: '/batch_calls',
    component: BatchCallListPage,
    icon: <BatchCallIcon />,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    pageTitle: 'Mass Outreach Tasks',

    url: '/batch_calls/task_wizard',
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: '/batch_calls/task_wizard/:task_id',
    component: CallTaskWizardPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    url: '/calls_management/:task_id',
    component: CallsManagementPage,
    action: PERMISSIONS.SUPER_ADMIN,
  },
  {
    menuTitle: MenuLabel.Logout,

    onClick: () =>
      logout({ logoutParams: { returnTo: window.location.origin } }),
    icon: <ExitToApp />,
  },
];

export const getUnauthMenuItems = (loginWithRedirect: Function): MenuItem[] => [
  {
    menuTitle: MenuLabel.Login,
    onClick: () => loginWithRedirect(),
    icon: null,
  },
];

export const useRoutes = (
  login: Function,
  logout: Function,
  isAuthenticated: boolean
) => {
  const authMenuItems = useMemo(
    () => getAuthenticatedMenuItems(logout),
    [logout]
  );

  const unauthMenuItems = useMemo(() => getUnauthMenuItems(login), [login]);

  const menuItems = isAuthenticated ? authMenuItems : unauthMenuItems;

  const [publicRoutes, privateRoutes] = useMemo(() => {
    const itemsWithRoutes = menuItems.filter(i => i.component && i.url);

    return [
      itemsWithRoutes.filter(i => i.isPublic),
      itemsWithRoutes.filter(i => !i.isPublic),
    ];
  }, [menuItems]);

  return { publicRoutes, privateRoutes, menuItems };
};
